import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'
import { detectHost } from './host/detectHost'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? 'https://pknsrgukddhtpiuvekth.supabase.co'
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''

if (!SUPABASE_ANON_KEY) {
  console.warn('[Palstro] VITE_SUPABASE_ANON_KEY is not set. Auth will not work.')
}

// When the page is loaded on a STOREFRONT host (subdomain OR /shop/:slug path),
// no STAFF authentication happens — the storefront runs on the dedicated customer
// client (storageKey 'palstro-customer-auth', see src/pages/storefront/customerClient.ts).
// So the shared client must NOT auto-consume auth tokens from the URL there: a
// customer's email confirm / password-recovery link lands on the shop with the
// token in the URL, and if this shared client grabbed it, useAuthInit() would
// treat the shopper as a staff session and route them into the orphan/no-workspace
// flow. Disabling detectSessionInUrl on storefront hosts hands that token to the
// customer client exclusively; platform hosts keep the normal detect behaviour.
const _onStorefrontHost =
  typeof window !== 'undefined' &&
  detectHost(
    window.location.hostname,
    window.location.pathname,
    import.meta.env.VITE_STOREFRONT_BASE_DOMAIN || 'palstro.com',
  ).mode === 'storefront'

let _trialExpiredCallback: (() => void) | null = null
export function setTrialExpiredHandler(fn: (() => void) | null) {
  _trialExpiredCallback = fn
}

let _isReadOnly = false
export function setReadOnly(state: boolean) { _isReadOnly = state }
export function getReadOnly() { return _isReadOnly }

function isWriteRPC(name: string): boolean {
  return (
    name.startsWith('post_') ||
    name.startsWith('cancel_') ||
    name.startsWith('reopen_') ||
    name.startsWith('process_') ||
    name.startsWith('create_') ||
    name.startsWith('update_') ||
    name.startsWith('delete_')
  )
}

export const _rawSupabase = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: !_onStorefrontHost,
  },
})

// Proxy that intercepts writes when trial has expired, leaving reads and auth untouched.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabase = new Proxy(_rawSupabase, {
  get(target, prop, receiver) {
    if (prop === 'from') {
      return function (table: keyof Database['public']['Tables']) {
        const builder = target.from(table)
        return new Proxy(builder, {
          get(b, p) {
            if (
              ['insert', 'update', 'delete', 'upsert'].includes(p as string) &&
              _isReadOnly
            ) {
              if (_trialExpiredCallback) _trialExpiredCallback()
              return () => Promise.reject(new Error('Trial expired — read-only mode'))
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const val = Reflect.get(b, p) as any
            if (typeof val === 'function') return val.bind(b)
            return val
          },
        })
      }
    }

    if (prop === 'rpc') {
      return function (name: string, ...args: unknown[]) {
        if (isWriteRPC(name) && _isReadOnly) {
          if (_trialExpiredCallback) _trialExpiredCallback()
          return Promise.reject(new Error('Trial expired — read-only mode'))
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (target.rpc as any)(name, ...args)
      }
    }

    return Reflect.get(target, prop, receiver)
  },
})
